import React, { type ReactNode, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import EditIcon from '@atlaskit/icon/core/migration/edit';
import AddIcon from '@atlaskit/icon/core/migration/add--emoji-custom';
import { token } from '@atlaskit/tokens';

import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { FieldDescription } from '@atlassian/jira-issue-field-description/src/ui/index.tsx';
import { FieldHeadingTitle } from '@atlassian/jira-issue-field-heading/src/styled.tsx';
import { ExpandCollapseToggle } from '@atlassian/jira-servicedesk-cmdb-expand-collapse-toggle/src/ui/index.tsx';
import type { CmdbObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import messages from './messages.tsx';

export type Props = {
	isExpanded: boolean;
	isEditing: boolean;
	isEditable: boolean;
	isParentHovered: boolean;
	issueKey?: IssueKey; // TODO - make issueKey mandatory in BENTO-11149,
	children: undefined | ReactNode;
	inlineErrorMessage: undefined | ReactNode;
	values: CmdbObject[];
	fieldId?: string; // TODO - make fieldId mandatory in BENTO-11149,
	testId?: string;
	onEditModeClick: (analyticsEvent: UIAnalyticsEvent) => void;
	onExpandCollapseClick: (analyticsEvent: UIAnalyticsEvent) => void;
};

export const CmdbFieldLabel = (props: Props) => {
	const { formatMessage } = useIntl();

	const handleEditModeClick = (
		event: SyntheticEvent<HTMLElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		props.onEditModeClick(analyticsEvent);
	};

	const buttonLabel = formatMessage(props.isExpanded ? messages.hideDetails : messages.showDetails);

	const hasValues = props.values.length > 0;
	const shouldShowAddButton = !props.isEditing && !hasValues && !props.inlineErrorMessage;
	const shouldShowEditButton = !props.isEditing && hasValues && !props.inlineErrorMessage;
	const shouldShowObjectCount = shouldShowEditButton && !props.isExpanded;

	return (
		<Wrapper>
			<Label data-testid="issue-field-cmdb-object.ui.label.label">
				<FieldHeadingTitle data-testid={props.testId}>{props.children}</FieldHeadingTitle>
				{props.issueKey !== undefined && props.fieldId !== undefined && (
					// @ts-expect-error label is required, please pass it through when working on this code.
					<FieldDescription issueKey={props.issueKey} fieldKey={props.fieldId} />
				)}
			</Label>
			{props.inlineErrorMessage}
			{shouldShowAddButton && props.isEditable && (
				<Button
					appearance="link"
					iconBefore={<AddIcon label="" color={token('color.link')} />}
					onClick={handleEditModeClick}
					testId="issue-field-cmdb-object.ui.label.button-add"
					interactionName="auto-generated.issue-field-cmdb-object.ui.label.button-add.button"
				>
					{formatMessage(messages.addObject)}
				</Button>
			)}
			{shouldShowObjectCount && (
				<ObjectCount data-testid="issue-field-cmdb-object.ui.label.object-count">
					{formatMessage(messages.objectCount, { count: props.values.length })}
				</ObjectCount>
			)}
			{shouldShowEditButton && props.isEditable && (
				<EditButtonWrapper visible={props.isParentHovered}>
					<Tooltip content={formatMessage(messages.editField)}>
						<Button
							appearance="subtle"
							iconBefore={
								<EditIcon
									spacing="spacious"
									label={formatMessage(messages.editField)}
									color={token('color.icon.subtle')}
								/>
							}
							onClick={handleEditModeClick}
							testId="issue-field-cmdb-object.ui.label.button-edit"
							interactionName="auto-generated.issue-field-cmdb-object.ui.label.button-edit.button1"
						/>
					</Tooltip>
				</EditButtonWrapper>
			)}
			{shouldShowEditButton && (
				<ExpandCollapseToggle
					isExpanded={props.isExpanded}
					label={buttonLabel}
					onClick={props.onExpandCollapseClick}
					testId="issue-field-cmdb-object.ui.label.expand-collapse-toggle"
				/>
			)}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${gridSize * 4}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const Label = styled.div({
	flexGrow: 1,
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditButtonWrapper = styled.div<{ visible: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	opacity: (props) => (props.visible ? '1' : '0'),
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ObjectCount = styled.div({
	marginRight: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const visibleForTesting = {
	EditButtonWrapper,
	ObjectCount,
};
